import { buildUrl } from '@amazeelabs/react-framework-bridge';
import {
  buildHtml,
  buildImage,
  buildLink,
} from '@amazeelabs/react-framework-bridge/gatsby';
import loadable from '@loadable/component';
import {
  BlockMediaGallery,
  BlockTeaserList,
  CookiePreferencesButton,
  Document,
  Figure,
  InfoBox,
  InfoBoxList,
  LinkList,
  Message,
  Podcast,
  PodcastButtons,
  RemoteVideo,
  RichText,
} from '@nuklear9/ui';
import { navigate } from 'gatsby';
import React, { ReactNodeArray } from 'react';

import { PageContext } from '../types';
import CompaniesList from './companies-list';
import { drupalImageData } from './drupal-image';
import { UnreachableCaseError } from './unreachable-case-error';

const SilverbackIframe = loadable(
  async () =>
    (await import('@amazeelabs/silverback-iframe/src')).SilverbackIframe,
);

export const buildContent = (
  content: Array<ContentBlockFragment | undefined>,
  pageContext: PageContext,
): ReactNodeArray | null => {
  return content.map((block, index) => {
    switch (block?.__typename) {
      case 'DrupalBlockHtml':
        return (
          <RichText key={index} Content={buildHtml(block.html)} prose={true} />
        );

      case 'DrupalBlockImage':
        if (!block.image.url) {
          return;
        }
        return (
          <Figure
            key={index}
            Image={buildImage({
              image: drupalImageData(
                block.image,
                'content',
                '(min-width: 1000px): 768px',
              ),
              alt: block.image.translation.alt,
            })}
            Link={({ children, ...props }) => (
              <a
                href={block.image.url || '#'}
                target="_blank"
                rel="noreferrer"
                {...props}
              >
                {children}
              </a>
            )}
            caption={block.caption}
            source={block.source}
          />
        );

      case 'DrupalBlockRemoteVideo':
        return (
          <RemoteVideo key={index} title={block.title} url={block.embedUrl} />
        );

      case 'DrupalBlockForm':
        return (
          <>
            <SilverbackIframe
              key={index}
              src={buildUrl([
                process.env.GATSBY_DRUPAL_URL,
                pageContext.locale,
                'form',
                block.id,
              ])}
              buildMessages={(htmlMessages: Array<string>) => (
                <div className="status-messages">
                  {htmlMessages.map((message, index) => {
                    const Html = buildHtml(message);
                    return (
                      <Message key={index}>
                        <Html />
                      </Message>
                    );
                  })}
                </div>
              )}
              redirect={(url, htmlMessages) =>
                navigate(
                  url,
                  htmlMessages
                    ? {
                        state: {
                          messages: htmlMessages,
                        },
                      }
                    : {},
                )
              }
              style={{
                width: '100%',
                minWidth: '100%',
                marginLeft: '-0.25em',
                marginRight: '-0.25em',
              }}
            />
          </>
        );

      case 'DrupalBlockLinkList':
        return (
          <LinkList
            className={'lg:max-w-3xl'}
            links={block.items.map((item) => ({
              id: item.linkUrl,
              title: item.linkText,
              description: item.description,
              Link: buildLink({ href: item.linkUrl }),
            }))}
          />
        );

      case 'DrupalBlockDocument':
        return (
          <Document
            title={block.title}
            caption={block.caption}
            size={block.size}
            mime={block.filemime}
            Link={buildLink({ href: block.url })}
          />
        );

      case 'DrupalBlockPodcast':
        return (
          <Podcast
            title={block.title}
            url={block.embedUrl}
            caption={block.caption}
            provider={block.provider}
            className="my-4 sm:my-8"
          />
        );

      case 'DrupalBlockInfoBox':
        return (
          <InfoBox
            Content={block.boxContent ? buildHtml(block.boxContent) : undefined}
            Title={block.boxHeading ? buildHtml(block.boxHeading) : undefined}
            Image={
              block.boxImage?.image.url
                ? buildImage({
                    image: drupalImageData(
                      block.boxImage?.image,
                      'content',
                      '240px',
                    ),
                    alt: block.boxImage?.image.translation.alt,
                  })
                : undefined
            }
          />
        );

      case 'DrupalBlockCompaniesList':
        return <CompaniesList pageContext={pageContext} />;

      case 'DrupalBlockIframe':
        return <iframe src={block.url} width="100%" className={'iframe'} />;

      case 'DrupalBlockHighlight':
        return (
          <InfoBoxList className={'mt-10'} title={block.highlightTitle}>
            {buildContent(block.highlightContent || [], pageContext)}
          </InfoBoxList>
        );

      case 'DrupalBlockTeaserList':
        return (
          <BlockTeaserList
            items={block.items.map((item) => ({
              Title: buildHtml(item!.title),
              Subtitle: item!.subtitleHtml
                ? buildHtml(item!.subtitleHtml)
                : undefined,
              Description: item!.descriptionHtml
                ? buildHtml(item!.descriptionHtml)
                : undefined,
              Image: item.teaserImage?.url
                ? buildImage({
                    image: drupalImageData(
                      item.teaserImage,
                      'teaser',
                      '(min-width: 600px): 410px, 100vw',
                    ),
                    alt: item.teaserImage.translation.alt,
                  })
                : undefined,
              Link: buildLink({ href: item.url }),
            }))}
          />
        );

      case 'DrupalBlockCookiePreferencesButton':
        return <CookiePreferencesButton key={index} label={block.buttonText} />;

      case 'DrupalBlockPodcastButtons':
        return (
          <PodcastButtons
            key={index}
            spotifyUrl={block.spotifyUrl}
            appleUrl={block.appleUrl}
          />
        );

      case 'DrupalBlockMediaGallery':
        return (
          <BlockMediaGallery
            displayType={block.displayType}
            images={
              block.images
                ?.filter((blockImage) => !!blockImage && !!blockImage.image)
                .map((blockImage) => ({
                  image: buildImage({
                    image: drupalImageData(
                      blockImage!.image!,
                      block.displayType === 'SLIDER' ? 'hero' : 'teaser',
                      '(min-width: 1000px): 768px',
                    ),
                    alt: blockImage!.image!.translation?.alt || '',
                  }),
                  caption: blockImage!.caption || '',
                  source: blockImage!.source || '',
                })) || []
            }
          />
        );

      default:
        throw new UnreachableCaseError(block);
    }
  });
};
